import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { useT } from '@/lib/i18n';

// ============================================================================
// StatusTransitionsBar — панель статусной модели формы: «Статус:» + ТЕКУЩИЙ
// статус + «...» (окно выбора доступного перехода). Кнопки переходов прямо на
// форме НЕ выводятся — выбор статуса идёт из окна (запрос пользователя).
// Доступные переходы считает СЕРВЕР (по ролям) — компонент только показывает.
// Чисто отображательный компонент.
// ============================================================================

interface StatusTransitionsBarProps {
  currentName?: string;
  currentCode?: string;
  transitions?: Array<{ fromCode: string; toCode: string; toName?: string }>;
  busy: boolean;
  disabled: boolean;
  hasRecord: boolean;
  onApply: (toCode: string) => void;
}

export function StatusTransitionsBar({ currentName, currentCode, transitions, busy, disabled, hasRecord, onApply }: StatusTransitionsBarProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const trs = transitions || [];
  const canPick = hasRecord && !disabled && trs.length > 0;
  return (
    <div className="flex items-center gap-2 border rounded-md px-3 py-2 bg-muted/20">
      <span className="text-xs font-medium text-muted-foreground">{t('status.label')}</span>
      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
        {currentName || currentCode || '—'}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-6 w-7 px-0 text-[13px] leading-none"
        onClick={() => setOpen(true)}
        disabled={busy || !canPick}
        title={trs.length === 0 ? t('status.no_transitions') : t('status.choose')}
      >
        …
      </Button>
      {busy && <span className="text-[10px] text-muted-foreground">{t('status.changing')}</span>}
      {open && (
        <UniversalWindow
          open
          title={t('status.choose_title')}
          width="w-[min(360px,94vw)]"
          height="h-[min(60vh,420px)]"
          resizable={false}
          onClose={() => setOpen(false)}
        >
          <div className="flex flex-col gap-1">
            {trs.map((tr) => (
              <button
                key={`${tr.fromCode}->${tr.toCode}`}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onApply(tr.toCode);
                }}
                disabled={busy}
                className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm hover:bg-accent/40 disabled:opacity-50"
              >
                <span>→ {tr.toName || tr.toCode}</span>
                <span className="text-[10px] text-muted-foreground">{tr.toCode}</span>
              </button>
            ))}
          </div>
        </UniversalWindow>
      )}
    </div>
  );
}
